// src/components/EditUserModal.jsx
import React, { useEffect, useState } from "react";
import { apiFetch } from "../api";
import HeightSelector from "./HeightSelector";
import { useLoading } from "../contexts/LoadingContext";
import { SaveIcon } from "./icons";
import RecentActivity from "./RecentActivity";

const init = {
  referenceId: "",
  fullName: "",
  gender: "",
  age: "",
  city: "",
  country: "Pakistan",
  contactNumber: "",
  profession: "",
  monthlyIncome: "",
  cast: "",
  caste: "",
  maritalStatus: "single",
  dob: "",
  height: "",
  heightFeet: "",
  heightInches: "",
  education: "",
  instituteOfQualification: "",
  // siblings counts are derived from structured `siblings` array
  siblings: [],
  fatherOccupation: "",
  motherOccupation: "",
  fatherDeceased: false,
  motherDeceased: false,
  houseSizeMarla: "",
  houseLocation: "",
  area: "",
  houseStatus: "",
  preferences: "",
  views: 0,
  status: "active",
  // Match preferences (used when this profile receives referrals)
  clientNotes: "",
  preferredGender: "any",
  preferredAgeMin: "",
  preferredAgeMax: "",
  preferredCities: "",
  preferredProfessions: "",
  preferredCastes: "",
  preferredHeightMinFeet: "",
  preferredHeightMinInches: "",
  preferredHeightMaxFeet: "",
  preferredHeightMaxInches: "",
};

export default function EditUserModal({
  isOpen,
  initialData,
  onClose,
  onSaved,
  // Optional override — used when a public user edits their own linked
  // profile via /public/auth/my-profile. Defaults to the bureau route.
  endpointPath,
  title = "Edit Profile",
}) {
  const [form, setForm] = useState(init);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const { setLoading: setGlobalLoading, setGlobalLoadingMessage } = useLoading();

  useEffect(() => {
    if (isOpen && initialData) {
      console.log("EditUserModal: Setting form with initialData:", initialData.height);
      setForm({
        referenceId: initialData.referenceId || "",
        fullName: initialData.fullName || "",
        // Show the DB reality rather than silently substituting "male" —
        // this is what surfaced the AddUserModal bug where empty gender
        // looked like "male" in the form and the badge was missing from
        // the card. Empty here means a select-prompt rather than a
        // guess.
        gender: initialData.gender || "",
        age: initialData.age?.toString() || "",
        city: initialData.city || "",
        country: initialData.country || "Pakistan",
        contactNumber: initialData.contactNumber || "",
        profession: initialData.profession || "",
        monthlyIncome: initialData.monthlyIncome?.toString() || "",
        cast: initialData.cast || initialData.caste || "",
        caste: initialData.caste || initialData.cast || "",
        maritalStatus: initialData.maritalStatus || "single",
        dob: initialData.dob
          ? initialData.dob.split
            ? initialData.dob.split("T")[0]
            : initialData.dob
          : "",
        height: initialData.height || "",
        heightFeet: initialData.heightFeet || "",
        heightInches: initialData.heightInches || "",
        education: initialData.education || "",
        instituteOfQualification: initialData.instituteOfQualification || "",
        // brothers/sisters counts are shown from the `siblings` array

        siblings: initialData.siblings || [],
        fatherOccupation: initialData.fatherOccupation || "",
        motherOccupation: initialData.motherOccupation || "",
        fatherDeceased: initialData.fatherDeceased ?? false,
        motherDeceased: initialData.motherDeceased ?? false,
        houseSizeMarla: initialData.houseSizeMarla ?? "",
        houseLocation: initialData.houseLocation || "",
        area: initialData.area || "",
        houseStatus: initialData.houseStatus || "",
        preferences: initialData.preferences || "",
        views: initialData.views ?? 0,
        status: initialData.status || "active",
        clientNotes: initialData.clientNotes || "",
        preferredGender: initialData.preferredGender || "any",
        preferredAgeMin:
          initialData.preferredAgeRange?.min !== undefined &&
          initialData.preferredAgeRange?.min !== null
            ? String(initialData.preferredAgeRange.min)
            : "",
        preferredAgeMax:
          initialData.preferredAgeRange?.max !== undefined &&
          initialData.preferredAgeRange?.max !== null
            ? String(initialData.preferredAgeRange.max)
            : "",
        preferredCities: Array.isArray(initialData.preferredCities)
          ? initialData.preferredCities.join(", ")
          : "",
        preferredProfessions: Array.isArray(initialData.preferredProfessions)
          ? initialData.preferredProfessions.join(", ")
          : "",
        preferredCastes: Array.isArray(initialData.preferredCastes)
          ? initialData.preferredCastes.join(", ")
          : "",
        preferredHeightMinFeet:
          typeof initialData.preferredHeightMin === "number"
            ? String(Math.floor(initialData.preferredHeightMin / 12))
            : "",
        preferredHeightMinInches:
          typeof initialData.preferredHeightMin === "number"
            ? String(initialData.preferredHeightMin % 12)
            : "",
        preferredHeightMaxFeet:
          typeof initialData.preferredHeightMax === "number"
            ? String(Math.floor(initialData.preferredHeightMax / 12))
            : "",
        preferredHeightMaxInches:
          typeof initialData.preferredHeightMax === "number"
            ? String(initialData.preferredHeightMax % 12)
            : "",
      });
    } else if (!isOpen) {
      setForm(init);
    }
  }, [initialData, isOpen]);

  const handleChange = (e) => {
    // Handle custom HeightSelector events
    if (e.target && e.target.heightFeet !== undefined && e.target.heightInches !== undefined) {
      // This is a HeightSelector event - handle specially
      const { value, heightFeet, heightInches } = e.target;
      setForm((p) => ({ 
        ...p, 
        height: value,
        heightFeet: heightFeet,
        heightInches: heightInches
      }));
      return;
    }
    
    // Handle normal form events
    const { name, value, type, checked } = e.target;
    const nextVal = type === "checkbox" ? checked : value;
    
    // If updating cast or caste, synchronize both fields
    if (name === "cast" || name === "caste") {
      setForm((p) => ({
        ...p,
        cast: nextVal,
        caste: nextVal
      }));
    } else if (name === "gender") {
      // Matrimonial pairing is always cross-gender — derive the
      // preferred gender from the profile's own gender. The bureau
      // can still override manually in the preferences section, but
      // the default reflects the obvious case so they don't have to.
      const opposite =
        nextVal === "male" ? "female" : nextVal === "female" ? "male" : null;
      setForm((p) => ({
        ...p,
        gender: nextVal,
        ...(opposite ? { preferredGender: opposite } : {}),
      }));
    } else {
      setForm((p) => ({ ...p, [name]: nextVal }));
    }
  };

  const handleAgeChange = (e) => {
    const raw = e.target.value;
    if (raw === "") {
      setForm((p) => ({ ...p, age: "" }));
      return;
    }
    const num = Number(raw);
    if (Number.isNaN(num)) return;
    const clamped = num > 100 ? 100 : num;
    setForm((p) => ({ ...p, age: String(clamped) }));
  };

  const handleAgeBlur = () => {
    if (form.age === "" || form.age === undefined || form.age === null) return;
    const num = Number(form.age);
    if (Number.isNaN(num)) return;
    if (num < 18) setForm((p) => ({ ...p, age: "18" }));
    else if (num > 100) setForm((p) => ({ ...p, age: "100" }));
  };

  // Preferred-age range clamping. Caps at 80 during typing; snaps to the
  // 18 floor on blur. Minus / plus / e are blocked at the keypress level.
  const handlePreferredAgeChange = (field) => (e) => {
    const raw = e.target.value;
    if (raw === "") {
      setForm((p) => ({ ...p, [field]: "" }));
      return;
    }
    const num = Number(raw);
    if (Number.isNaN(num)) return;
    const clamped = num > 80 ? 80 : num < 0 ? 0 : num;
    setForm((p) => ({ ...p, [field]: String(clamped) }));
  };
  const handlePreferredAgeBlur = (field) => () => {
    const v = form[field];
    if (v === "" || v === undefined || v === null) return;
    const num = Number(v);
    if (Number.isNaN(num)) return;
    if (num < 18) setForm((p) => ({ ...p, [field]: "18" }));
    else if (num > 80) setForm((p) => ({ ...p, [field]: "80" }));
  };
  const blockNonNumericKey = (e) => {
    if (["-", "+", "e", "E"].includes(e.key)) e.preventDefault();
  };

  const handleSiblingChange = (index, field, value) => {
    setForm((p) => {
      const next = { ...p };
      next.siblings = next.siblings ? [...next.siblings] : [];
      next.siblings[index] = {
        ...(next.siblings[index] || {}),
        [field]: value,
      };
      return next;
    });
  };

  const addSibling = () => {
    setForm((p) => ({
      ...p,
      siblings: [
        ...(p.siblings || []),
        {
          relation: "brother",
          qualification: "",
          profession: "",
          maritalStatus: "",
        },
      ],
    }));
  };

  const removeSibling = (index) => {
    setForm((p) => {
      const next = { ...p };
      next.siblings = [...(next.siblings || [])];
      next.siblings.splice(index, 1);
      return next;
    });
  };

  const validate = () => {
    // Gender required — historical records without a value can't be
    // saved back empty (otherwise the card has no badge + matchmaking
    // can't pair the profile).
    if (!form.gender) {
      return "Please select gender";
    }

    // Contact number is now OPTIONAL. Many bureaus keep it off-platform
    // and handle introductions manually after approval. We only validate
    // format when a value is supplied.
    const phone = form.contactNumber?.trim() || "";
    if (phone && !/^\d{11}$/.test(phone)) {
      return "Contact number must be exactly 11 digits when provided";
    }

    // Age, when provided, must be 18-100
    if (form.age !== "" && form.age !== undefined && form.age !== null) {
      const ageNum = Number(form.age);
      if (Number.isNaN(ageNum) || ageNum < 18 || ageNum > 100) {
        return "Age must be between 18 and 100";
      }
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setAttemptedSubmit(true);
    const v = validate();
    if (v) {
      setFormError(v);
      return;
    }

    const siblingsArray = Array.isArray(form.siblings) ? form.siblings : [];
    const computedBrothers = siblingsArray.filter(
      (s) => s.relation === "brother"
    ).length;
    const computedSisters = siblingsArray.filter(
      (s) => s.relation === "sister"
    ).length;

    const csvToArray = (s) =>
      typeof s === "string"
        ? s.split(",").map((x) => x.trim()).filter(Boolean)
        : [];

    const {
      preferredAgeMin,
      preferredAgeMax,
      preferredCities,
      preferredProfessions,
      preferredCastes,
      preferredHeightMinFeet,
      preferredHeightMinInches,
      preferredHeightMaxFeet,
      preferredHeightMaxInches,
      ...rest
    } = form;

    const toTotalInches = (ft, inch) => {
      const f = ft === "" || ft === null || ft === undefined ? null : Number(ft);
      const i =
        inch === "" || inch === null || inch === undefined ? null : Number(inch);
      if ((f === null || Number.isNaN(f)) && (i === null || Number.isNaN(i))) {
        return undefined;
      }
      return (Number.isFinite(f) ? f : 0) * 12 + (Number.isFinite(i) ? i : 0);
    };

    const payload = {
      ...rest,
      // Optimistic-concurrency guard. Server compares against the live
      // document version and 409s if another member edited in the meantime.
      __v: initialData?.__v,
      age: Number(form.age),
      siblings: siblingsArray,
      brothersCount: computedBrothers,
      sistersCount: computedSisters,
      houseSizeMarla: form.houseSizeMarla
        ? Number(form.houseSizeMarla)
        : undefined,
      views: Number(form.views || 0),
      dob: form.dob || undefined,
      preferredAgeRange:
        preferredAgeMin || preferredAgeMax
          ? {
              min: preferredAgeMin ? Number(preferredAgeMin) : undefined,
              max: preferredAgeMax ? Number(preferredAgeMax) : undefined,
            }
          : undefined,
      preferredCities: csvToArray(preferredCities),
      preferredProfessions: csvToArray(preferredProfessions),
      preferredCastes: csvToArray(preferredCastes),
      preferredHeightMin: toTotalInches(
        preferredHeightMinFeet,
        preferredHeightMinInches
      ),
      preferredHeightMax: toTotalInches(
        preferredHeightMaxFeet,
        preferredHeightMaxInches
      ),
    };

    try {
      setIsSaving(true);
      setGlobalLoadingMessage("Updating profile...");
      setGlobalLoading(true);
      
      const url = endpointPath || `/profiles/${initialData._id}`;
      await apiFetch(url, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      onSaved && onSaved();
    } catch (err) {
      console.error("Update failed:", err);
      // 409 → another member updated this profile while the form was open.
      // Surface a clear reload prompt; the server returns the latest doc
      // under `payload.latest` so a confirmed reload re-seeds the form.
      if (err?.status === 409 && err.payload?.latest) {
        const shouldReload = window.confirm(
          (err.message || "This profile was updated by another member.") +
            "\n\nClick OK to reload the latest version (your unsaved changes will be replaced)."
        );
        if (shouldReload && typeof onSaved === "function") {
          // onSaved triggers the parent to re-fetch + reopen. Pass a hint so
          // it can show a toast if it wants to.
          onSaved({ conflict: true });
        }
        setFormError("Reloaded to latest version. Re-apply your changes.");
      } else {
        setFormError(err.message || "Failed to update user");
      }
    } finally {
      setIsSaving(false);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="relative w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden rounded-xl bg-[var(--color-surface)] shadow-lg">
        {/* HEADER — always visible */}
        <div className="flex items-start justify-between px-4 sm:px-6 py-4 border-b border-[var(--color-border)] flex-shrink-0">
          <h3 className="text-lg font-semibold text-[var(--color-text-strong)]">
            {title}
          </h3>
          <button
            onClick={onClose}
            className="text-sm font-semibold text-[var(--color-text)]"
          >
            Close
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col flex-1 overflow-hidden"
        >
          {/* BODY — scrolls; only the form fields move */}
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
            {formError && (
              <div className="rounded-lg border border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger-text)]">
                {formError}
              </div>
            )}
          {/* Reference ID — bureau-side only. Hidden when this modal is
              used as the seeker's "Edit Your Profile" (endpointPath set),
              because the bureau owns this identifier. Editable in case the
              bureau needs to align it with an external roster, fix a typo,
              etc. Server enforces sparse-unique on save; an 11000 error
              will surface via the existing error display. */}
          {!endpointPath && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                Reference ID
              </label>
              <input
                name="referenceId"
                value={form.referenceId}
                onChange={handleChange}
                placeholder="e.g. IMB30955"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-3 py-2 text-sm font-mono"
              />
              <div className="mt-1 text-xs text-[var(--color-text-muted)]">
                Must be unique across all profiles. Changing this affects
                anywhere the bureau looks up this candidate by ID.
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-muted)]">
              Full Name
            </label>
            <input
              name="fullName"
              value={form.fullName}
              onChange={handleChange}
              className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
            />
            <div className="mt-1 text-sm text-red-500"></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                Age
              </label>
              <input
                name="age"
                type="number"
                min="18"
                max="100"
                value={form.age}
                onChange={handleAgeChange}
                onBlur={handleAgeBlur}
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              />
              <div className="mt-1 text-sm text-red-500">
                {attemptedSubmit && form.age !== "" && (Number(form.age) < 18 || Number(form.age) > 100)
                  ? "Age must be between 18 and 100"
                  : ""}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                Date of Birth
              </label>
              <input
                name="dob"
                type="date"
                value={form.dob}
                onChange={handleChange}
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                Gender
              </label>
              <select
                name="gender"
                value={form.gender}
                onChange={handleChange}
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              >
                <option value="" disabled>
                  Select gender
                </option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
              <div className="mt-1 text-sm text-red-500"></div>
            </div>
            <div>
              
              <HeightSelector
                value={{'heightFeet':form.heightFeet  , 'heightInches':form.heightInches }}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                Marital Status
              </label>
              <select
                name="maritalStatus"
                value={form.maritalStatus}
                onChange={handleChange}
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              >
                <option value="single">Single</option>
                <option value="married">Married</option>
                <option value="divorced">Divorced</option>
                <option value="separated">Separated</option>
                <option value="widowed">Widowed</option>
              </select>
              <div className="mt-1 text-sm text-red-500"></div>
            </div>
          </div>
          <div className="border-t border-[var(--color-border)]"></div>
          <div>
            Caste
            <label className="block text-sm font-medium text-[var(--color-text-muted)]">
              Cast
            </label>
            <input
              name="cast"
              value={form.cast}
              onChange={handleChange}
              className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
            />
          </div>
          <div className="border-t border-[var(--color-border)]"></div>
          <div>
            <div className="text-sm font-medium text-[var(--color-text-muted)] mb-2">House Information</div>
            <label className="block text-sm font-medium text-[var(--color-text-muted)]">
              House Status
            </label>
            <select
              name="houseStatus"
              value={form.houseStatus}
              onChange={handleChange}
              className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
            >
              <option value="">Select</option>
              <option value="rent">Rent</option>
              <option value="own">Own</option>
            </select>
            <div className="mt-1 text-sm text-red-500"></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                House Size (Marla)
              </label>
              <input
                name="houseSizeMarla"
                type="number"
                min="0"
                value={form.houseSizeMarla}
                onChange={handleChange}
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              />
              <div className="mt-1 text-sm text-red-500"></div>
            </div>
            <div>
            <label className="block text-sm font-medium text-[var(--color-text-muted)]">
              City
            </label>
            <input
              name="city"
              value={form.city}
              onChange={handleChange}
              className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              placeholder="Enter city"
            />
            <div className="mt-1 text-sm text-red-500"></div>
          </div>
            
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                Country
              </label>
              <input
                name="country"
                value={form.country}
                onChange={handleChange}
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
                placeholder="e.g. Pakistan"
              />
            </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-muted)]">
              Area
            </label>
            <input
              name="area"
              value={form.area}
              onChange={handleChange}
              className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              placeholder="Enter area"
            />
            <div className="mt-1 text-sm text-red-500"></div>
          </div>
          </div>
          <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                Further Details
              </label>
              <input
                name="houseLocation"
                value={form.houseLocation}
                onChange={handleChange}
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
                placeholder="block or phase "
              />
              <div className="mt-1 text-sm text-red-500"></div>
            </div>
          <div className="border-t border-[var(--color-border)] "></div>
          <div>
            Education Details
            <label className="block text-sm font-medium text-[var(--color-text-muted)]">
              Profession
            </label>
            <input
              name="profession"
              value={form.profession}
              onChange={handleChange}
              className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
            />
            <div className="mt-1 text-sm text-red-500"></div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-muted)]">
              Monthly Income (Rs)
            </label>
            <input
              type="text"
              name="monthlyIncome"
              value={form.monthlyIncome}
              onChange={handleChange}
              onKeyPress={(e) => {
                if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                  e.preventDefault();
                }
              }}
              className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              placeholder="Enter amount in rupees"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-muted)]">
              Education
            </label>
            <input
              name="education"
              value={form.education}
              onChange={handleChange}
              className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
            />
          </div>
          <div></div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-muted)]">
              Institute of Education
            </label>
            <input
              name="instituteOfQualification"
              value={form.instituteOfQualification}
              onChange={handleChange}
              className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
            />
          </div>
          <div className="border-t border-[var(--color-border)] mt-4 pt-4"></div>
          Family Details
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-[var(--color-text-muted)]">
              Siblings
            </label>
            <button
              type="button"
              onClick={addSibling}
              className="text-sm text-[var(--color-primary)]"
            >
              + Add Sibling
            </button>
          </div>
          {(form.siblings || []).map((s, i) => (
            <div key={i} className="mt-2 space-y-2">
              <div className="grid grid-cols-2 gap-2 items-end">
                <div>
                  <label className="text-xs text-[var(--color-text-muted)]">
                    Relation
                  </label>
                  <select
                    value={s.relation}
                    onChange={(e) => handleSiblingChange(i, "relation", e.target.value)}
                    className="w-full rounded-lg border px-2 py-1 text-sm"
                  >
                    <option value="brother">Brother</option>
                    <option value="sister">Sister</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[var(--color-text-muted)]">
                    Qualification
                  </label>
                  <input
                    value={s.qualification}
                    onChange={(e) => handleSiblingChange(i, "qualification", e.target.value)}
                    className="w-full rounded-lg border px-2 py-1 text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 items-end">
                <div>
                  <label className="text-xs text-[var(--color-text-muted)]">
                    Profession
                  </label>
                  <input
                    value={s.profession}
                    onChange={(e) => handleSiblingChange(i, "profession", e.target.value)}
                    className="w-full rounded-lg border px-2 py-1 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-[var(--color-text-muted)]">
                    Marital Status
                  </label>
                  <div className="flex gap-2 items-center">
                    <select
                      value={s.maritalStatus || ""}
                      onChange={(e) => handleSiblingChange(i, "maritalStatus", e.target.value)}
                      className="w-full rounded-lg border px-2 py-1 text-sm"
                    >
                      <option value="">Select</option>
                      <option value="single">Single</option>
                      <option value="married">Married</option>
                      <option value="divorced">Divorced</option>
                      <option value="separated">Separated</option>
                      <option value="widowed">Widowed</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => removeSibling(i)}
                      className="rounded px-2 py-1 text-sm border"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div className="border-b border-[var(--color-border)] mt-4 pb-4"></div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                Father Occupation
              </label>
              <input
                name="fatherOccupation"
                value={form.fatherOccupation}
                onChange={handleChange}
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              />
              <div className="mt-1 text-sm text-red-500">{attemptedSubmit && !form.fatherOccupation ? "" : ""}</div>
              <div className="mt-2 text-xs">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="fatherDeceased"
                    checked={!!form.fatherDeceased}
                    onChange={handleChange}
                  />
                  <span className="text-[var(--color-text-muted)]">
                    Father deceased
                  </span>
                </label>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                Mother Occupation
              </label>
              <input
                name="motherOccupation"
                value={form.motherOccupation}
                onChange={handleChange}
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              />
              <div className="mt-1 text-sm text-red-500">{attemptedSubmit && !form.motherOccupation ? "" : ""}</div>
              <div className="mt-2 text-xs">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="motherDeceased"
                    checked={!!form.motherDeceased}
                    onChange={handleChange}
                  />
                  <span className="text-[var(--color-text-muted)]">
                    Mother deceased
                  </span>
                </label>
              </div>
            </div>
          </div>
          <div className="border-t border-[var(--color-border)] mt-4 pt-4"></div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-muted)]">
              Preferences
            </label>
            <input
              name="preferences"
              value={form.preferences}
              onChange={handleChange}
              className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              placeholder="eg no foreign proposals, looking for someone with education etc"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                Contact Number{" "}
                <span className="text-xs font-normal opacity-70">(optional)</span>
              </label>
              <input
                name="contactNumber"
                type="text"
                inputMode="numeric"
                pattern="\d{11}"
                maxLength={11}
                value={form.contactNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, ""); // remove non-digits
                  if (value.length <= 11) {
                    setForm({ ...form, contactNumber: value });
                  }
                }}
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
                placeholder="03003331333"
              />
              <div className="mt-1 text-xs text-[var(--color-text-muted)]">
                Leave blank to keep contact off-platform — you'll introduce
                both parties manually when approving requests.
              </div>
            </div>
          </div>

          {/* ===== Match Preferences (used when this profile receives referrals) ===== */}
          <div className="pt-2">
            <h4 className="text-sm font-semibold text-[var(--color-text-strong)]">
              Match Preferences
            </h4>
            <p className="text-xs text-[var(--color-text-muted)] mb-2">
              Used when matching this profile to others. Comma-separate multiple values.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                  Preferred Gender
                </label>
                <select
                  name="preferredGender"
                  value={form.preferredGender}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                >
                  <option value="any">Any</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                    Min Age
                  </label>
                  <input
                    type="number"
                    name="preferredAgeMin"
                    value={form.preferredAgeMin}
                    onChange={handlePreferredAgeChange("preferredAgeMin")}
                    onBlur={handlePreferredAgeBlur("preferredAgeMin")}
                    onKeyDown={blockNonNumericKey}
                    min="18"
                    max="80"
                    inputMode="numeric"
                    className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                    Max Age
                  </label>
                  <input
                    type="number"
                    name="preferredAgeMax"
                    value={form.preferredAgeMax}
                    onChange={handlePreferredAgeChange("preferredAgeMax")}
                    onBlur={handlePreferredAgeBlur("preferredAgeMax")}
                    onKeyDown={blockNonNumericKey}
                    inputMode="numeric"
                    min="18"
                    max="80"
                    className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                  Preferred Cities
                </label>
                <input
                  type="text"
                  name="preferredCities"
                  value={form.preferredCities}
                  onChange={handleChange}
                  placeholder="Lahore, Karachi"
                  className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                  Preferred Professions
                </label>
                <input
                  type="text"
                  name="preferredProfessions"
                  value={form.preferredProfessions}
                  onChange={handleChange}
                  placeholder="Engineer, Doctor"
                  className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                  Preferred Castes
                </label>
                <input
                  type="text"
                  name="preferredCastes"
                  value={form.preferredCastes}
                  onChange={handleChange}
                  placeholder="Awan, Rajput"
                  className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-1">
                  Preferred Height Range
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-[var(--color-text-muted)] mb-1">Min</div>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        name="preferredHeightMinFeet"
                        value={form.preferredHeightMinFeet}
                        onChange={handleChange}
                        min="0"
                        max="8"
                        placeholder="ft"
                        className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                      />
                      <input
                        type="number"
                        name="preferredHeightMinInches"
                        value={form.preferredHeightMinInches}
                        onChange={handleChange}
                        min="0"
                        max="11"
                        placeholder="in"
                        className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-[var(--color-text-muted)] mb-1">Max</div>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        name="preferredHeightMaxFeet"
                        value={form.preferredHeightMaxFeet}
                        onChange={handleChange}
                        min="0"
                        max="8"
                        placeholder="ft"
                        className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                      />
                      <input
                        type="number"
                        name="preferredHeightMaxInches"
                        value={form.preferredHeightMaxInches}
                        onChange={handleChange}
                        min="0"
                        max="11"
                        placeholder="in"
                        className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                  Internal Notes
                </label>
                <textarea
                  name="clientNotes"
                  value={form.clientNotes}
                  onChange={handleChange}
                  rows={2}
                  className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Bureau-side recent activity. Skip for the public-user reuse,
              which talks to /public/auth/my-profile and has no audit feed. */}
          {initialData?._id && !endpointPath && (
            <RecentActivity resource="User" resourceId={initialData._id} />
          )}

          </div>
          {/* FOOTER — always visible. Cancel removed per request; the
              header "Close" button + Escape / overlay click still exit
              the modal without saving. */}
          <div className="flex justify-end gap-3 px-4 sm:px-6 py-4 border-t border-[var(--color-border)] bg-[var(--color-surface)] flex-shrink-0">
            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] disabled:opacity-50 px-4 py-2 text-sm font-semibold shadow-sm transition-colors"
            >
              <SaveIcon className="w-4 h-4" />
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
